// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect } from 'react';

import { t } from '@/i18n';
import { normalizeHoleEntry, parseHandicapValue } from '@/features/hole/utils';
import type { HoleEntry, Player, TeamSummary } from '@/features/hole/types';

type SessionUser = {
  id: string;
  token?: string | null;
};

type UseHoleDataParams = {
  apiBaseUrl: string;
  user: SessionUser | null;
  allowGuestClaimMode: boolean;
  claimPlayerId?: string | null;
  roundId: string | null;
  roundIdIsValid: boolean;
  isNavigationReady: boolean;
  setLoading: (value: boolean) => void;
  setError: (value: string | null) => void;
  setPlayers: (players: Player[]) => void;
  setTourId: (tourId: string) => void;
  setTeams: (teams: TeamSummary[]) => void;
  setLoggedInPlayerId: (value: string | null) => void;
  setAllHoleEntries: (entries: HoleEntry[]) => void;
  logWithContext: (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => void;
};

export const useHoleData = ({
  apiBaseUrl,
  user,
  allowGuestClaimMode,
  claimPlayerId,
  roundId,
  roundIdIsValid,
  isNavigationReady,
  setLoading,
  setError,
  setPlayers,
  setTourId,
  setTeams,
  setLoggedInPlayerId,
  setAllHoleEntries,
  logWithContext,
}: UseHoleDataParams) => {
  const userToken = user?.token ?? null;
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!isNavigationReady || !roundId) return;
    if (allowGuestClaimMode) {
      setLoading(false);
      setError(null);
      return;
    }
    if (!userToken) {
      setLoading(false);
      return;
    }

    const fetchPlayers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
          headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
        });
        if (!response.ok) {
          throw new Error();
        }

        const data = await response.json();
        const list = (Array.isArray(data) ? data : (data.players ?? data.Players ?? [])) as Array<Player & Record<string, unknown>>;
        const normalizedPlayers = list.map((player) => {
          const raw = player as {
            handicap?: unknown;
            Handicap?: unknown;
            hcp?: unknown;
            Hcp?: unknown;
            playingHandicap?: unknown;
            playing_handicap?: unknown;
            imageKey?: string | null;
            ImageKey?: string | null;
            imageThumbKey?: string | null;
            ImageThumbKey?: string | null;
          };
          const handicap = parseHandicapValue(
            raw.handicap ?? raw.Handicap ?? raw.hcp ?? raw.Hcp ?? raw.playingHandicap ?? raw.playing_handicap
          );
          const imageKey = raw.imageKey ?? raw.ImageKey ?? null;
          const imageThumbKey = raw.imageThumbKey ?? raw.ImageThumbKey ?? null;
          return { ...player, handicap, imageKey, imageThumbKey };
        });
        setPlayers(normalizedPlayers);

        const incomingTourId = (data as { tourId?: string; TourId?: string }).tourId ?? (data as { TourId?: string }).TourId;
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
      } catch {
        if (!allowGuestClaimMode) {
          setError(t('hole.playersLoadFailed'));
        }
      } finally {
        setLoading(false);
      }
    };

    void fetchPlayers();
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, roundId, setError, setLoading, setPlayers, setTourId, userToken]);

  useEffect(() => {
    if (!isNavigationReady || !roundIdIsValid || !roundId) return;
    if (allowGuestClaimMode) {
      return;
    }
    if (!userToken) {
      return;
    }

    const fetchTeams = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/groups`, {
          headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
        });
        if (!response.ok) {
          throw new Error(`Failed to load groups (${response.status})`);
        }

        const data = (await response.json()) as {
          groups?: Array<{
            groupId?: string;
            GroupId?: string;
            teams?: Array<{
              teamId?: string;
              TeamId?: string;
              playerIds?: string[];
              PlayerIds?: string[];
            }>;
            Teams?: Array<{
              teamId?: string;
              TeamId?: string;
              playerIds?: string[];
              PlayerIds?: string[];
            }>;
          }>;
          Groups?: Array<{
            groupId?: string;
            GroupId?: string;
            teams?: Array<{
              teamId?: string;
              TeamId?: string;
              playerIds?: string[];
              PlayerIds?: string[];
            }>;
            Teams?: Array<{
              teamId?: string;
              TeamId?: string;
              playerIds?: string[];
              PlayerIds?: string[];
            }>;
          }>;
        };

        const groups = data.groups ?? data.Groups ?? [];
        const mappedTeams: TeamSummary[] = groups.flatMap((group, groupIndex) => {
          const teams = group.teams ?? group.Teams ?? [];
          return teams.map((team, teamIndex) => ({
            id: team.teamId ?? team.TeamId ?? `${group.groupId ?? group.GroupId ?? groupIndex}:${teamIndex}`,
            players: team.playerIds ?? team.PlayerIds ?? [],
          }));
        });

        setTeams(mappedTeams);
        logWithContext('hole.teams.loaded', {
          message: 'Loaded group-derived teams for round.',
          teamCount: mappedTeams.length,
        });
      } catch (err) {
        logWithContext(
          'hole.teams.error',
          { message: err instanceof Error ? err.message : 'Failed to load group-derived teams.' },
          'warn'
        );
        setTeams([]);
      }
    };

    void fetchTeams();
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, logWithContext, roundId, roundIdIsValid, setTeams, userToken]);

  useEffect(() => {
    if (allowGuestClaimMode && claimPlayerId) {
      setLoggedInPlayerId(claimPlayerId);
      return;
    }

    if (!userId || !userToken) {
      setLoggedInPlayerId(null);
      return;
    }

    let cancelled = false;
    const fetchCurrentPlayer = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${userId}`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (!response.ok) {
          if (!cancelled) {
            setLoggedInPlayerId(null);
          }
          return;
        }
        const payload = (await response.json()) as { id?: string };
        if (!cancelled) {
          setLoggedInPlayerId(payload.id ?? null);
        }
      } catch {
        if (!cancelled) {
          setLoggedInPlayerId(null);
        }
      }
    };

    void fetchCurrentPlayer();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, claimPlayerId, setLoggedInPlayerId, userId, userToken]);

  useEffect(() => {
    if (!roundId || !isNavigationReady) {
      setAllHoleEntries([]);
      return;
    }
    if (allowGuestClaimMode) {
      setAllHoleEntries([]);
      return;
    }
    if (!userToken) {
      setAllHoleEntries([]);
      return;
    }

    let cancelled = false;
    const fetchAllHoleEntries = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-entries`, {
          headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
        });
        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as Array<Record<string, unknown>>;
        const normalized = payload
          .map((entry) => normalizeHoleEntry(entry))
          .filter((entry) => entry.holeNumber > 0);
        if (!cancelled) {
          setAllHoleEntries(normalized);
        }
      } catch {
        // Keep menu functional with websocket-only updates if this request fails.
      }
    };

    void fetchAllHoleEntries();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, roundId, setAllHoleEntries, userToken]);
};
